import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './OrderForm.css';
import { 
  ArrowLeftIcon, 
  TimerIcon, 
  CopyIcon, 
  CheckIcon
} from './Icons';

const OrderForm = ({ selectedCrypto, onBack }) => {
  const [step, setStep] = useState(1); // 1: Details, 2: Payment, 3: Processing, 4: Success
  const [orderId, setOrderId] = useState('');
  const [processing, setProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [newOrderLoading, setNewOrderLoading] = useState(false);
  const [formData, setFormData] = useState({
    flashAmount: '',
    walletAddress: '',
    name: '',
    email: '',
    telegramContact: '',
    couponCode: '',
    transactionHash: '',
    screenshot: null
  });
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [paymentData, setPaymentData] = useState({
    qrCode: '',
    paymentWallet: '',
    amountToPay: 0
  });
  const [timeLeft, setTimeLeft] = useState(1800); // 30 minutes in seconds
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [discount, setDiscount] = useState(0);
  const [minOrderError, setMinOrderError] = useState(false);

  // Generate Order ID
  useEffect(() => {
    if (!orderId) {
      const newOrderId = 'FU' + Date.now() + Math.floor(Math.random() * 1000);
      setOrderId(newOrderId);
    }
  }, [orderId]);

  // Countdown Timer
  useEffect(() => {
    if (step === 2 && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [step, timeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear minimum order error when user types
    if (name === 'flashAmount') {
      setMinOrderError(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, screenshot: file }));
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setFormData(prev => ({ ...prev, screenshot: file }));
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const removeScreenshot = () => {
    setFormData(prev => ({ ...prev, screenshot: null }));
    setScreenshotPreview(null);
  };

  const applyCoupon = () => {
    if (formData.couponCode.toUpperCase() === 'FLASH10') {
      setDiscount(10);
      setMessage({ type: 'success', text: '10% discount applied!' });
    } else if (formData.couponCode.toUpperCase() === 'FLASH20') {
      setDiscount(20);
      setMessage({ type: 'success', text: '20% discount applied!' });
    } else {
      setDiscount(0);
      setMessage({ type: 'error', text: 'Invalid coupon code' });
    }
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const calculateTotal = () => {
    const flashAmount = parseFloat(formData.flashAmount) || 0;
    const usdtAmount = flashAmount / 10; // 1 USDT = 10 Flash USDT
    const discountAmount = (usdtAmount * discount) / 100;
    return (usdtAmount - discountAmount).toFixed(2);
  };

  const proceedToPayment = () => {
    const flashAmount = parseFloat(formData.flashAmount);
    const orderValue = flashAmount / 10; // 10 Flash USDT = 1 USDT
    
    if (flashAmount < 250 || orderValue < 25) {
      setMinOrderError(true);
      setMessage({ type: '', text: '' });
      
      // Scroll to the error message
      setTimeout(() => {
        const inputElement = document.getElementById('flashAmountInput');
        if (inputElement) {
          inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }
    if (!formData.walletAddress || !formData.name || !formData.email) {
      setMessage({ type: 'error', text: 'Please fill all required fields' });
      setMinOrderError(false);
      return;
    }

    // Show loading state
    setCheckoutLoading(true);
    setMessage({ type: '', text: '' });
    setMinOrderError(false);

    // Simulate processing delay
    setTimeout(() => {
      // Set payment wallet and QR code based on selected crypto
      const wallets = {
        'Polygon': '0x2DEe775b21C8300F15206469D4Ec51905b4cf61b',
        'USDT TRC20': 'TLPCefrPhPEDaeGt1a6eyDm3uV9AvfhHeF',
        'USDT ERC20': '0x2DEe775b21C8300F15206469D4Ec51905b4cf61b',
        'USDT BEP20': '0x2DEe775b21C8300F15206469D4Ec51905b4cf61b'
      };

      const qrCodes = {
        'Polygon': '/PolygonUSDtQR.jpg', // Polygon doesn't have QR yet
        'USDT TRC20': '/TRC0qr.jpg',
        'USDT ERC20': '/ERC20QR.jpg',
        'USDT BEP20': '/BEP20QR.jpg'
      };

      setPaymentData({
        qrCode: qrCodes[selectedCrypto.name] || '/qr-code-placeholder.png',
        paymentWallet: wallets[selectedCrypto.name] || 'YOUR_WALLET_ADDRESS',
        amountToPay: calculateTotal()
      });

      setStep(2);
      setTimeLeft(1800); // Reset timer
      setCheckoutLoading(false);
    }, 1500); // 1.5 second delay
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Show processing screen
    setStep(3);
    setProcessing(true);
    setProcessingProgress(0);

    // Simulate processing with progress
    const progressInterval = setInterval(() => {
      setProcessingProgress(prev => {
        if (prev >= 95) {
          clearInterval(progressInterval);
          return 95;
        }
        return prev + 5;
      });
    }, 200);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('orderId', orderId);
      formDataToSend.append('crypto', selectedCrypto.name);
      formDataToSend.append('flashAmount', formData.flashAmount);
      formDataToSend.append('usdtAmount', calculateTotal());
      formDataToSend.append('walletAddress', formData.walletAddress);
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('telegramContact', formData.telegramContact);
      formDataToSend.append('couponCode', formData.couponCode);
      formDataToSend.append('discount', discount);
      formDataToSend.append('transactionHash', formData.transactionHash);
      if (formData.screenshot) {
        formDataToSend.append('screenshot', formData.screenshot);
      }

      const response = await axios.post(`${process.env.REACT_APP_API_URL || ''}/api/submit-order`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        // Complete progress
        clearInterval(progressInterval);
        setProcessingProgress(100);
        
        // Wait a moment before showing success
        setTimeout(() => {
          setProcessing(false);
          setStep(4);
        }, 800);
      }
    } catch (error) {
      clearInterval(progressInterval);
      setProcessing(false);
      setStep(2); // Go back to payment step
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to submit order. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="order-form-section">
      <div className="container">
        {step !== 3 && step !== 4 && (
          <button className="back-btn" onClick={onBack}>
            <ArrowLeftIcon size={18} /> Back to Selection
          </button>
        )}
        
        {/* STEP 1: Order Details */}
        {step === 1 && (
          <div className="order-container">
            <div className="order-form-card">
              <div className="selected-crypto-header">
                <img src={selectedCrypto.icon} alt={selectedCrypto.name} />
                <h2>{selectedCrypto.name}</h2>
                <p className="order-id">Order ID: {orderId}</p>
                <div className="exchange-rate">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{marginRight: '8px'}}>
                    <path d="M7 10l5 5 5-5M7 14l5 5 5-5" stroke="#00d4ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  1 USDT = 10 Flash USDT
                </div>
              </div>

              <div className="form-group">
                <label>Flash USDT Amount *</label>
                <input
                  type="number"
                  name="flashAmount"
                  value={formData.flashAmount}
                  onChange={handleChange}
                  placeholder="Minimum 250 Flash USDT"
                  required
                  min="250"
                  className={minOrderError ? 'error-input' : ''}
                  id="flashAmountInput"
                />
                <small>
                  {formData.flashAmount ? (
                    <>Current value: ${(parseFloat(formData.flashAmount) / 10).toFixed(2)} USDT</>
                  ) : (
                    <>Current value: $0.00 USDT</>
                  )}
                </small>
                {minOrderError && (
                  <div className="min-order-error">
                    ⚠ Minimum order value is $25 USDT. Please enter at least 250 Flash USDT.
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Your Wallet Address (to receive Flash USDT) *</label>
                <input
                  type="text"
                  name="walletAddress"
                  value={formData.walletAddress}
                  onChange={handleChange}
                  placeholder="Enter your wallet address"
                  required
                />
              </div>

              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>Telegram Contact (Optional)</label>
                <input
                  type="text"
                  name="telegramContact"
                  value={formData.telegramContact}
                  onChange={handleChange}
                  placeholder="@username or phone number"
                />
                <small>For faster order updates and support</small>
              </div>
            </div>

            <div className="order-summary-card">
              <h3>Order Summary</h3>
              <div className="summary-row">
                <span>Flash USDT Amount:</span>
                <span>{formData.flashAmount || 0} FUSDT</span>
              </div>
              <div className="summary-row">
                <span>USDT to Pay:</span>
                <span>{(parseFloat(formData.flashAmount) / 10 || 0).toFixed(2)} USDT</span>
              </div>
              
              <div className="coupon-section">
                <input
                  type="text"
                  name="couponCode"
                  value={formData.couponCode}
                  onChange={handleChange}
                  placeholder="Enter coupon code"
                  className="coupon-input"
                />
                <button type="button" onClick={applyCoupon} className="apply-btn">
                  Apply
                </button>
              </div>

              {discount > 0 && (
                <div className="summary-row discount">
                  <span>Discount ({discount}%):</span>
                  <span>-{((parseFloat(formData.flashAmount) / 10) * discount / 100).toFixed(2)} USDT</span>
                </div>
              )}

              <div className="summary-row total">
                <span>Total Amount:</span>
                <span>{calculateTotal()} USDT</span>
              </div>

              {message.text && (
                <div className={`message ${message.type}`}>
                  {message.text}
                </div>
              )}

              <button 
                type="button" 
                onClick={proceedToPayment} 
                className="checkout-btn"
                disabled={checkoutLoading}
              >
                {checkoutLoading ? (
                  <>
                    <span className="button-spinner"></span>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Proceed to Checkout
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Payment */}
        {step === 2 && (
          <div className="payment-container">
            <div className="payment-card">
              <h2>Complete Payment</h2>

              <div className="timer-section">
                <div className="timer-display">
                  <span className="timer-icon"><TimerIcon size={32} color="#8a2be2" /></span>
                  <span className="timer-text">{formatTime(timeLeft)}</span>
                </div>
                <div className="timer-bar">
                  <div 
                    className="timer-progress" 
                    style={{ width: `${(timeLeft / 1800) * 100}%` }}
                  ></div>
                </div>
                <p className="timer-warning">Complete payment within 30 minutes</p>
              </div>

              <div className="payment-details">
                <div className="qr-section">
                  <div className="qr-code-container">
                    <img 
                      src={paymentData.qrCode} 
                      alt="Payment QR Code" 
                      className="qr-code-image"
                    />
                    <p className="qr-text">Scan to Pay</p>
                  </div>
                  
                  <div className="payment-info-compact">
                    <div className="info-row">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke="#00d4ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span className="info-label">Order ID:</span>
                      <span className="info-value">{orderId}</span>
                    </div>
                    
                    <div className="info-row amount-row">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="#ffd700" strokeWidth="2"/>
                        <path d="M12 6v12M9 9h4.5a1.5 1.5 0 010 3H9M9 15h4.5a1.5 1.5 0 000-3H9" stroke="#ffd700" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      <span className="info-label">Amount:</span>
                      <span className="info-value amount-highlight">{paymentData.amountToPay} USDT</span>
                    </div>
                  </div>
                  
                  <div className="network-warning">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{flexShrink: 0}}>
                      <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 17l10 5 10-5M2 12l10 5 10-5" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Send funds only via <strong>{selectedCrypto.name}</strong> network, otherwise funds will be lost</span>
                  </div>
                </div>

                <div className="wallet-section">
                  <label>Payment Wallet Address:</label>
                  <div className="wallet-address">
                    <input 
                      type="text" 
                      value={paymentData.paymentWallet} 
                      readOnly 
                    />
                    <button 
                      onClick={() => navigator.clipboard.writeText(paymentData.paymentWallet)}
                      className="copy-btn"
                    >
                      <CopyIcon size={16} /> Copy
                    </button>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="payment-form">
                <div className="form-group">
                  <label>Transaction Hash *</label>
                  <input
                    type="text"
                    name="transactionHash"
                    value={formData.transactionHash}
                    onChange={handleChange}
                    placeholder="Enter transaction hash"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Upload Payment Screenshot *</label>
                  <div 
                    className="file-upload-area"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={() => document.getElementById('screenshot-input').click()}
                  >
                    {!screenshotPreview ? (
                      <>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="#00d4ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <p className="upload-text">Drop your screenshot here or <span>click to browse</span></p>
                        <p className="upload-hint">PNG, JPG up to 10MB</p>
                      </>
                    ) : (
                      <div className="screenshot-preview">
                        <img src={screenshotPreview} alt="Screenshot preview" />
                        <div className="preview-overlay">
                          <p className="file-name-preview">✓ {formData.screenshot.name}</p>
                          <button 
                            type="button" 
                            className="remove-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeScreenshot();
                            }}
                          >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                              <path d="M18 6L6 18M6 6l12 12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            Remove
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    id="screenshot-input"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    required
                  />
                </div>

                {message.text && (
                  <div className={`message ${message.type}`}>
                    {message.text}
                  </div>
                )}

                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="button-spinner"></span>
                      Processing Order...
                    </>
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Place Order
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* STEP 3: Processing */}
        {step === 3 && processing && (
          <div className="processing-container">
            <div className="processing-card">
              <div className="processing-spinner">
                <div className="spinner-ring"></div>
                <div className="spinner-ring"></div>
                <div className="spinner-ring"></div>
              </div>
              
              <h2>Processing Your Order</h2>
              <p className="processing-subtitle">Please wait while we verify your payment...</p>
              
              <div className="processing-steps">
                <div className={`processing-step ${processingProgress >= 20 ? 'active' : ''}`}>
                  <div className="step-check">
                    {processingProgress >= 20 ? <CheckIcon size={16} color="#00d4ff" /> : '1'}
                  </div>
                  <span>Verifying Payment</span>
                </div>
                <div className={`processing-step ${processingProgress >= 50 ? 'active' : ''}`}>
                  <div className="step-check">
                    {processingProgress >= 50 ? <CheckIcon size={16} color="#00d4ff" /> : '2'}
                  </div>
                  <span>Confirming Transaction</span>
                </div>
                <div className={`processing-step ${processingProgress >= 80 ? 'active' : ''}`}>
                  <div className="step-check">
                    {processingProgress >= 80 ? <CheckIcon size={16} color="#00d4ff" /> : '3'}
                  </div>
                  <span>Processing Order</span>
                </div>
              </div>

              <div className="progress-bar-container">
                <div className="progress-bar" style={{ width: `${processingProgress}%` }}></div>
              </div>
              <p className="progress-text">{processingProgress}% Complete</p>
              
              <p className="processing-note">
                <strong>Order ID:</strong> {orderId}
              </p>
            </div>
          </div>
        )}

        {/* STEP 4: Success */}
        {step === 4 && (
          <div className="success-container">
            <div className="success-card">
              <div className="success-icon">
                <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                  <circle cx="40" cy="40" r="38" fill="url(#successGradient)" opacity="0.15"/>
                  <circle cx="40" cy="40" r="30" fill="url(#successGradient)"/>
                  <path d="M25 40l10 10 20-20" stroke="#ffffff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                  <defs>
                    <linearGradient id="successGradient" x1="0" y1="0" x2="80" y2="80">
                      <stop offset="0%" stopColor="#00d4ff"/>
                      <stop offset="100%" stopColor="#0099cc"/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <h2>Order Placed Successfully!</h2>
              <p className="order-id-large">Order ID: {orderId}</p>
              
              <div className="processing-notice">
                <div className="notice-icon">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                    <circle cx="20" cy="20" r="18" stroke="#00d4ff" strokeWidth="2.5"/>
                    <path d="M20 12v10M20 28h.02" stroke="#00d4ff" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <div className="notice-content">
                  <h3>Please Wait - Your Flash USDT is Processing</h3>
                  <p>
                    Our team is currently verifying your transaction ID and payment screenshot. 
                    This verification process may take some time to ensure security and accuracy.
                  </p>
                </div>
              </div>
              
              <div className="success-info">
                <div className="info-card">
                  <div className="info-icon">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                      <circle cx="20" cy="20" r="18" stroke="#00d4ff" strokeWidth="2.5"/>
                      <path d="M20 10v10l6 6" stroke="#00d4ff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="info-text">
                    <strong>Delivery Time</strong>
                    <p>Your Flash USDT will reflect in your wallet within <span className="highlight-text">30 minutes to 1 hour</span></p>
                  </div>
                </div>
                
                <div className="info-card">
                  <div className="info-icon">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                      <circle cx="20" cy="20" r="18" stroke="#10b981" strokeWidth="2.5"/>
                      <circle cx="20" cy="20" r="8" stroke="#10b981" strokeWidth="2.5"/>
                      <path d="M20 2v6M20 32v6M2 20h6M32 20h6" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div className="info-text">
                    <strong>Verification in Progress</strong>
                    <p>Our team is verifying your payment details for security purposes</p>
                  </div>
                </div>
                
                <div className="info-card">
                  <div className="info-icon">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                      <rect x="6" y="10" width="28" height="24" rx="3" stroke="#ffd700" strokeWidth="2.5"/>
                      <path d="M6 18h28M12 24h6M12 28h12" stroke="#ffd700" strokeWidth="2.5" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div className="info-text">
                    <strong>Processing Time</strong>
                    <p>Delivery time depends on current request volume. High demand may cause slight delays</p>
                  </div>
                </div>

                <div className="wallet-info">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{marginRight: '10px', verticalAlign: 'middle'}}>
                    <rect x="2" y="6" width="20" height="14" rx="2" stroke="#00d4ff" strokeWidth="2"/>
                    <circle cx="17" cy="13" r="2" stroke="#00d4ff" strokeWidth="2"/>
                    <path d="M6 6V4a2 2 0 012-2h8a2 2 0 012 2v2" stroke="#00d4ff" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <strong>Receiving Wallet:</strong>
                  <p>{formData.walletAddress}</p>
                </div>
                <div className="amount-info">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{marginRight: '10px', verticalAlign: 'middle'}}>
                    <circle cx="12" cy="12" r="10" stroke="#ffd700" strokeWidth="2"/>
                    <path d="M12 6v12M9 9h4.5a1.5 1.5 0 010 3H9M9 15h4.5a1.5 1.5 0 000-3H9" stroke="#ffd700" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <strong>Flash USDT Amount:</strong>
                  <p>{formData.flashAmount} FUSDT</p>
                </div>
              </div>

              <div className="success-actions">
                <button 
                  onClick={() => {
                    setNewOrderLoading(true);
                    setTimeout(() => {
                      window.location.reload();
                    }, 1000);
                  }} 
                  className="new-order-btn"
                  disabled={newOrderLoading}
                >
                  {newOrderLoading ? (
                    <>
                      <span className="button-spinner"></span>
                      Loading...
                    </>
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      Place New Order
                    </>
                  )}
                </button>
                <button onClick={onBack} className="home-btn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Back to Home
                </button>
              </div>

              <p className="confirmation-email">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{marginRight: '8px', verticalAlign: 'middle'}}>
                  <rect x="2" y="4" width="20" height="16" rx="2" stroke="#00d4ff" strokeWidth="2"/>
                  <path d="M2 7l10 7 10-7" stroke="#00d4ff" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                A confirmation email has been sent to {formData.email}
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default OrderForm;
